/**
 * `<GameCanvas>` — branchement réel du moteur Phaser (M1-08, contrat étendu M2-07).
 *
 * Le composant ne fait que transmettre : contenu publié, état d'autorité serveur, sélection et
 * rappels. Deux réglages valent d'être connus :
 * - `hudDansCanvas={false}` : le HUD vit **uniquement** en React, alimenté par les réponses du
 *   serveur (A-12). Un second HUD dessiné dans le canvas pourrait afficher une valeur locale ;
 * - le conteneur garde ses attributs `data-*` (niveau, emplacements, vagues, ennemis), utiles aux
 *   tests et au diagnostic.
 */
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import type { GameCanvasProps } from './types';

/**
 * Chargement **différé** du moteur : Phaser pèse ≈ 1,4 Mo et n'est utile qu'une fois la run créée.
 * L'écran d'accueil et les formulaires ne le téléchargent donc plus (chunk séparé).
 */
const MoteurJeuDynamique = lazy(async () => {
  const module = await import('../../game');
  return { default: module.MoteurJeu };
});

export function GameCanvas({
  levelId,
  contenu,
  etatRun,
  tourSelectionnee,
  vitesse,
  onPoseDemandee,
  onTourSelectionnee,
  onVagueTerminee,
  onPvPerdus,
  onPartiePerdue,
  onPret,
  onTourRefusee,
  surMoteur,
}: GameCanvasProps) {
  const { t } = useTranslation();

  return (
    <div
      className="canvas"
      role="group"
      aria-label={t('jeu.canvas.titre')}
      data-niveau={levelId}
      data-emplacements={contenu.niveau.emplacements.length}
      data-vagues={contenu.niveau.vagues.length}
      data-tours={contenu.tours.length}
      data-ennemis={contenu.ennemis.length}
      data-eclats-run={etatRun.eclatsRun}
      data-jokers-run={etatRun.jokers}
      data-tour-selectionnee={tourSelectionnee ?? ''}
      data-vitesse={vitesse ?? 1}
    >
      <Suspense fallback={null}>
        <MoteurJeuDynamique
          levelId={levelId}
          contenu={contenu}
          etatRun={etatRun}
          tourSelectionnee={tourSelectionnee}
          onPoseDemandee={onPoseDemandee}
          onVagueTerminee={onVagueTerminee}
          onPvPerdus={onPvPerdus}
          onPret={onPret}
          hudDansCanvas={false}
          {...(vitesse === undefined ? {} : { vitesse })}
          {...(onTourSelectionnee === undefined ? {} : { onTourSelectionnee })}
          {...(onPartiePerdue === undefined ? {} : { onPartiePerdue })}
          {...(onTourRefusee === undefined ? {} : { onTourRefusee })}
          {...(surMoteur === undefined ? {} : { surMoteur })}
        />
      </Suspense>
    </div>
  );
}
